import React from "react";
//#region redux
//#endregion
//#region types
import { ChoiceProps, SelectInputProps, TextInputProps } from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
import { CustomerConstants, IconDefinitionConstants } from "utils/constants";
import { SendSettingsSectionProps } from "models/propTypes/customerPropTypes";
import { CustomerEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import {
  HorizontalFormField,
  GroupSection,
  SelectInput,
  FormFieldLayout,
  VerticalFormField,
  TextInput,
  TextAreaInput,
  Choice,
} from "components/common/wintForms";
import { useCustomerService, useInvoiceService } from "serviceHooks";
import { Grid } from "@mui/material";
import { ActionLinkButton, Icon } from "components/common";
import FromStandardIcon from "./fromStandardIcon";
import InheritedOrOverriddenText from "./inheritedOrOverridenText";
//#endregion
import { EnumHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const SendSettingsSection: React.FC<SendSettingsSectionProps> = ({ connect, invoiceSettings }) => {
  const customerService = useCustomerService();
  const invoiceService = useInvoiceService();

  const connectSettingType = connect("sendOptions.settingType");
  const isCompanySpecificSettings = connectSettingType.value === CustomerEnums.CustomerSettingType.CustomerSpecific;

  const connectSendType = connect("sendOptions.invoiceSendType");
  const connectTitleTemplate = connect("sendOptions.invoiceTitleTemplate");
  const connectMessageTemplate = connect("sendOptions.invoiceMessageTemplate");
  const connectFileNameTemplate = connect("sendOptions.invoiceFileNameTemplate");

  const connectSendToType = connect("sendOptions.invoiceSendTo");
  const connectInvoiceNotesTemplate = connect("sendOptions.invoiceNotesTemplate");

  return (
    <GroupSection
      header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.InvoiceManagement)}
      icon={IconDefinitionConstants.WPaperPlane}
      navKey={CustomerConstants.SettingsNavKeys.InvoiceManagement}
    >
      <HorizontalFormField<ChoiceProps>
        Component={Choice}
        {...connectSettingType}
        label={TranslationKeys.Customer.InvoiceSending}
        descriptionText={
          <Grid container direction="column" alignItems="start" className={cssExports.formControlDescription}>
            <Grid item>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.InvoiceSendingDescription)}
            </Grid>
            <ActionLinkButton
              endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} />}
              className={cssExports.descriptionLink}
              href={UrlHelper.formatPath(`/Settings?section=invoice`)}
            >
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.GoToStandardSettings)}
            </ActionLinkButton>
          </Grid>
        }
        items={customerService.getSettingTypes()}
      />
      {isCompanySpecificSettings && (
        <>
          <HorizontalFormField<SelectInputProps>
            Component={SelectInput}
            {...connectSendType}
            label={TranslationKeys.Customer.InvoiceDeliveryMethod}
            descriptionText={TranslationKeys.Customer.InvoiceDeliveryMethodDescription}
            items={EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceSendMethod).map((item) => ({
              value: item,
              label: invoiceService.getSendAsTypeText(item),
            }))}
            endAdornment={<FromStandardIcon value={connectSendType.value} />}
            fieldValueDynamicDescriptionText={
              <InheritedOrOverriddenText
                standardValue={
                  invoiceSettings?.defaultInvoiceSendOption !== undefined
                    ? invoiceService.getSendAsTypeText(invoiceSettings.defaultInvoiceSendOption as number)
                    : undefined
                }
                value={connectSendType.value}
              />
            }
            optional
          />
          {connectSendType.value === WintSuperkollAPIModel.InvoiceSendMethod.Email && (
            <>
              <FormFieldLayout direction="horizontal" label={TranslationKeys.Customer.InvoiceSendToType}>
                <Grid container direction="column" rowGap={1}>
                  <VerticalFormField<SelectInputProps>
                    Component={SelectInput}
                    {...connectSendToType}
                    label={TranslationKeys.Customer.InvoiceSendToType}
                    items={
                      EnumHelper.getNumericItemValues(WintSuperkollAPIModel.SendInvoiceMailsToEnum).map((item) => ({
                        value: item,
                        label: LocalizationHelperInstance.getTranslatedEnumValue(
                          "Invoice.SendInvoiceMailsTo",
                          WintSuperkollAPIModel.SendInvoiceMailsToEnum[item]
                        ),
                      })) ?? []
                    }
                    optional
                    noOuterSpacing
                  />
                  {connectSendToType.value === WintSuperkollAPIModel.SendInvoiceMailsToEnum.Other && (
                    <VerticalFormField<TextInputProps>
                      Component={TextInput}
                      {...connect("sendOptions.invoiceSendToEmail")}
                      label={TranslationKeys.Customer.InvoiceCustomSendToEmail}
                      noOuterSpacing
                    />
                  )}
                </Grid>
              </FormFieldLayout>
            </>
          )}
          <FormFieldLayout
            direction="horizontal"
            label={TranslationKeys.Settings.InvoiceMail}
            descriptionText={TranslationKeys.Settings.InvoiceMailDescription}
          >
            <Grid container direction="column" rowGap={2}>
              <VerticalFormField<TextInputProps>
                Component={TextInput}
                {...connectTitleTemplate}
                label={TranslationKeys.Common.Subject}
                noOuterSpacing
                endAdornment={<FromStandardIcon value={connectTitleTemplate.value} />}
                fieldValueDynamicDescriptionText={
                  <InheritedOrOverriddenText
                    standardValue={invoiceSettings?.invoiceTitleTemplate}
                    value={connectTitleTemplate.value}
                  />
                }
                optional
              />
              <VerticalFormField<TextInputProps>
                Component={TextAreaInput}
                {...connectMessageTemplate}
                label={TranslationKeys.Common.Message}
                maxRows={5}
                minRows={2}
                noOuterSpacing
                endAdornment={<FromStandardIcon value={connectMessageTemplate.value} />}
                fieldValueDynamicDescriptionText={
                  <InheritedOrOverriddenText
                    standardValue={invoiceSettings?.invoiceMessageTemplate}
                    value={connectMessageTemplate.value}
                  />
                }
                optional
              />
            </Grid>
          </FormFieldLayout>
          <HorizontalFormField<TextInputProps>
            Component={TextInput}
            {...connectFileNameTemplate}
            label={TranslationKeys.Settings.FileNameTemplate}
            descriptionText={TranslationKeys.Settings.FileNameTemplateDescription}
            endAdornment={<FromStandardIcon value={connectFileNameTemplate.value} />}
            fieldValueDynamicDescriptionText={
              <InheritedOrOverriddenText
                standardValue={invoiceSettings?.invoiceFileNameTemplate}
                value={connectFileNameTemplate.value}
              />
            }
            optional
          />
          <HorizontalFormField<TextInputProps>
            Component={TextAreaInput}
            {...connectInvoiceNotesTemplate}
            label={TranslationKeys.Settings.InvoiceMessage}
            descriptionText={TranslationKeys.Settings.InvoiceMessageDescription}
            endAdornment={<FromStandardIcon value={connectInvoiceNotesTemplate.value} />}
            fieldValueDynamicDescriptionText={
              <InheritedOrOverriddenText
                standardValue={invoiceSettings?.defaultNotes}
                value={connectInvoiceNotesTemplate.value}
              />
            }
            optional
          />
        </>
      )}
    </GroupSection>
  );
};

export default SendSettingsSection;
